import { inject, Service } from '@angular/core';
import FingerprintJS, {
  Agent,
  ExtendedGetResult,
  LoadOptions,
} from '@fingerprintjs/fingerprintjs-pro';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CONSTANTS } from '../../../common/constants/constants';
import { UtilsService } from '../util/utils.service';

const FALLBACK_VISITOR_ID_PREFIX = 'mock-';

/**
 * A visitorId is stable per device, so the TTL only bounds how long we trust a
 * stored one. Kept short because the value is client-writable and the fraud
 * checks that consume it work over hours, not weeks; Safari evicts
 * script-writable storage after 7 days anyway, so this costs us little.
 */
const VISITOR_ID_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** The SDK defaults to 10s, which is far longer than the survey can wait on it. */
const IDENTIFICATION_TIMEOUT_MS = 5000;

@Service()
export class FingerprintService {
  private utilsService = inject(UtilsService);

  private identificationRequest$?: Observable<ExtendedGetResult>;
  private agent?: Promise<Agent>;

  /**
   * The stable device id — all that `?f=` and every `*.visitorId` payload field
   * need. A device we have already identified is served from storage, so it
   * costs no further billable identification.
   */
  visitorId$(): Observable<string> {
    const qaResult = this.getQaResult();

    if (qaResult) {
      return of(qaResult.visitorId);
    }

    const cachedVisitorId = this.readCachedVisitorId();

    return cachedVisitorId
      ? of(cachedVisitorId)
      : this.identification$().pipe(map(fingerprint => fingerprint.visitorId));
  }

  /**
   * The full identification, and the only method that can issue a billable
   * `fp.get()`. It is deliberately not served from cache: the analytics payload
   * is stored under a hash of every field it contains, so replaying one would
   * collide rows across unrelated venues and surveys. Only call it where the
   * extended fields are actually sent.
   */
  identification$(): Observable<ExtendedGetResult> {
    const qaResult = this.getQaResult();

    if (qaResult) {
      return of(qaResult);
    }

    // One shared identification per document: subscriber count no longer drives
    // call count, and late subscribers replay the settled result.
    this.identificationRequest$ ??= defer(() => this.resolveIdentification()).pipe(
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.identificationRequest$;
  }

  /**
   * Fetches the agent script without identifying anyone. `load()` is not billable —
   * only `get()` is — so warming it at page load keeps the script fetch off the
   * critical path when the identification finally runs at submit.
   */
  preload(): void {
    if (this.getQaResult()) {
      return;
    }

    const agent = this.loadAgent();

    // Marks the rejection handled; resolveIdentification awaits the same promise
    // and deals with the failure properly.
    void agent.catch(() => undefined);
  }

  private async resolveIdentification(): Promise<ExtendedGetResult> {
    try {
      const agent = await this.loadAgent();
      const fingerprint: ExtendedGetResult = await agent.get({
        extendedResult: true,
        timeout: IDENTIFICATION_TIMEOUT_MS,
      });

      this.setFingerPrintInLocalStorages(fingerprint.visitorId);

      return fingerprint;
    } catch (error) {
      this.logIdentificationError(error);

      // A validated cached id beats a fresh fallback: it keeps the visitor
      // operative, where a `mock-` prefix makes the backend archive their
      // answers and refuse their prize. It also keeps the id we store matching
      // the one every other call site is already sending.
      const visitorId = this.readCachedVisitorId() ?? this.getFallbackVisitorId();

      return this.createMockExtendedGetResult(visitorId);
    }
  }

  private loadAgent(): Promise<Agent> {
    return (this.agent ??= this.createAgent());
  }

  private async createAgent(): Promise<Agent> {
    const token = environment.fingerprintToken;

    if (!token) {
      throw new Error('The fingerprint token is not configured for this environment');
    }

    return FingerprintJS.load(this.getLoadOptions(token));
  }

  private getLoadOptions(token: string): LoadOptions {
    const scriptUrlPattern = environment.fingerprintProxyScript
      ? [environment.fingerprintProxyScript, FingerprintJS.defaultScriptUrlPattern]
      : undefined;
    const endpoint = environment.fingerprintProxyEndpoint
      ? [environment.fingerprintProxyEndpoint, FingerprintJS.defaultEndpoint]
      : environment.fingerprintEndpoint;

    return environment.production
      ? {
          apiKey: token,
          region: 'eu',
          endpoint,
          scriptUrlPattern,
        }
      : {
          token,
          region: 'eu',
          endpoint,
          scriptUrlPattern,
        };
  }

  /**
   * Always log the SDK's own message. Quota exhaustion, wrong region, forbidden
   * origin, an expired key and a CSP block are only distinguishable through it, so
   * without it a spike in fallback ids looks identical to an ad blocker.
   */
  private logIdentificationError(error: unknown): void {
    const message = this.getErrorMessage(error);

    if (this.isFpClientTimeoutError(error)) {
      console.warn('FP timed out, using fallback visitorId:', message);
    } else if (this.isFpAgentScriptLoadError(error)) {
      console.warn(
        'FP agent script blocked (often an ad blocker or privacy tool), using fallback visitorId:',
        message
      );
    } else {
      console.error('FP identification failed, using fallback visitorId:', message, error);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const { message } = error;

      return typeof message === 'string' ? message : '';
    }

    return typeof error === 'string' ? error : '';
  }

  private isFpClientTimeoutError(error: unknown): boolean {
    return this.getErrorMessage(error) === FingerprintJS.ERROR_CLIENT_TIMEOUT;
  }

  private isFpAgentScriptLoadError(error: unknown): boolean {
    return this.getErrorMessage(error)
      .toLowerCase()
      .includes('failed to load the js script of the agent');
  }

  private readCachedVisitorId(): string | null {
    try {
      const visitorId = localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK_VAL);

      // A fallback id must never be served as a real identification: the backend
      // treats the prefix as an inoperative visitor and archives the answers.
      if (!visitorId || visitorId.startsWith(FALLBACK_VISITOR_ID_PREFIX)) {
        return null;
      }

      if (!this.isFpCorrect(visitorId)) {
        return null;
      }

      // An entry written before the timestamp existed reads as 0, so it expires
      // and is replaced by a fresh identification exactly once.
      const storedAt = Number(localStorage.getItem(CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK_AT));

      return Date.now() - storedAt < VISITOR_ID_CACHE_TTL_MS ? visitorId : null;
    } catch {
      return null;
    }
  }

  private setFingerPrintInLocalStorages(visitorId: string): void {
    // Never let persistence discard an identification we have already paid for:
    // storage throws when it is blocked or full, which is the private-mode case.
    try {
      const hashCode = this.utilsService.getHashCode(this.getKeyValue(visitorId));

      if (hashCode === null) {
        return;
      }

      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK, hashCode.toString());
      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK_VAL, visitorId);
      localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK_AT, Date.now().toString());
    } catch (error) {
      console.warn('FP could not be persisted to localStorage:', error);
    }
  }

  private getKeyValue(visitorId: string): string {
    return `${visitorId}&${environment.alHp}&${visitorId}${environment.alHp}`;
  }

  /**
   * Guards against a corrupted or hand-edited entry only. `alHp` is a public
   * constant compiled into the bundle, so this is not a tamper boundary —
   * bumping it in the environment files is, however, the kill switch: it
   * invalidates every cached entry in the field on the next deploy.
   */
  private isFpCorrect(visitorId: string): boolean {
    return this.utilsService.isHashKeyCorrect(
      this.getKeyValue(visitorId),
      CONSTANTS.LOCALSTORAGE_KEYS.FP_FOODBACK
    );
  }

  /**
   * The QA bypass needs both halves armed. Emitting a null visitorId when only
   * the flag is set makes the survey submit 400 and the analytics POST 403.
   */
  private getQaResult(): ExtendedGetResult | null {
    const qaToken = environment.fH ? environment.fH.toString() : null;
    const isArmed =
      qaToken !== null && !!environment.fpQA && localStorage.getItem(environment.fpQA) === qaToken;
    const testVisitorId = localStorage.getItem('testFingerprint');

    return isArmed && testVisitorId
      ? {
          ...this.createMockExtendedGetResult(testVisitorId),
          requestId: new Date().getTime().toString(),
        }
      : null;
  }

  /**
   * One fallback id per tab. The wheel-spin and phone-verification endpoints
   * compare the visitorId byte-for-byte against a previously stored value, so a
   * fresh id on every page load costs the guest their prize.
   */
  private getFallbackVisitorId(): string {
    try {
      const stored = sessionStorage.getItem(CONSTANTS.SESSIONSTORAGE_KEYS.FP_FALLBACK);

      if (stored) {
        return stored;
      }
    } catch {
      // Storage blocked — fall through to an id stable for this document only.
    }

    const visitorId = `${FALLBACK_VISITOR_ID_PREFIX}${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;

    try {
      sessionStorage.setItem(CONSTANTS.SESSIONSTORAGE_KEYS.FP_FALLBACK, visitorId);
    } catch {
      // As above.
    }

    return visitorId;
  }

  private createMockExtendedGetResult(visitorId: string): ExtendedGetResult {
    const now = new Date().toISOString();

    return {
      visitorId,
      incognito: false,
      browserName: 'Unknown',
      browserVersion: '0.0.0',
      device: 'Other',
      ip: '0.0.0.0',
      os: 'Unknown OS',
      osVersion: '0.0',
      firstSeenAt: {
        global: now,
        subscription: now,
      },
      lastSeenAt: {
        global: now,
        subscription: now,
      },
      confidence: {
        score: 0,
      },
      requestId: '',
      visitorFound: false,
    };
  }
}
